import React, { useState, useEffect, useMemo } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import CRNAProfileCard from "../Profile/CRNAProfileCard";
import FlippableListingCard from "../Profile/FlippableListingCard";
import { transformListingData } from "../../utils/listingTransformUtils";
import { getBackendUrl } from "../../utils/apiUrl";
import negotiationLink from "../../utils/negotiationLink";
import { apiErrorMessage } from "../../utils/apiErrorMessage";
import { AVAILABILITY_NEEDS_CONFIRMATION } from "../Profile/utils";
import "./AnonymousSwipeClaimModal.css";

const BACKEND_URL = getBackendUrl();

/**
 * Plain-language sentence per GateBlockedReason
 * (backend/src/matching/gate/pair-gate.util.ts:56). The gate hands back the
 * code; this is the ONLY place it becomes a sentence, so the none-match branch
 * can name the real reason instead of the old blanket guess at "location, pay
 * minimum, or provider type" -- which was wrong for four of the five codes.
 *
 * Each sentence is written about ONE listing, so it reads correctly whether it
 * is shown for a single listing or under a "3 listings:" count label.
 */
export const BLOCKED_REASON_SENTENCES = {
  "no-listing-geo": "the listing has no usable address",
  "comp-blind": "the listing does not state pay",
  "held-for-review": "the rate is being reviewed by us",
  "provider-no-floor": "the provider has not published a minimum",
  "provider-no-geo": "the provider has not set a location",
  // The two RELATIONSHIP reasons (recruiter-engagement.gate.ts,
  // EngagementBlockedReason). Not gate verdicts: these listings may fit this
  // provider perfectly and are still un-offerable because this company already
  // used them on this provider. A negotiation IS a Swipe row keyed
  // (provider, listing, initiatedBy), so the same listing can never carry a
  // second one -- which is why both sentences point at a DIFFERENT listing
  // rather than inviting a retry that cannot work.
  "negotiation-open": "you already have a conversation open with them on it",
  "negotiation-closed": "you already worked that role with them and it was closed out",
};

/**
 * The gate reports null when a listing failed on distance or on the pay
 * comparison rather than on one of its hard blocks, so those two ordinary
 * outcomes share this fallback. Also covers a code this build has never heard
 * of, which is the whole reason the map is looked up rather than indexed
 * blindly.
 *
 * IT MUST NOT NAME WHICH OF THE TWO FAILED, and must not name the two at all.
 * This used to read "the pay or the distance did not line up", which hands a
 * recruiter the shape of a provider's private terms: the floor is deliberately
 * blind (they get a yes or a no from the rate check and never a number), and a
 * sentence naming the axes turns a repeatable action into a probe -- re-post at
 * a higher rate, watch the reason change, and the number falls out. The other
 * sentences in the map are safe because they are facts about the RECRUITER'S
 * OWN listing, which they already know.
 *
 * The heading above it already says the listings do not fit. That is the whole
 * of what the recruiter is entitled to here.
 */
export const UNKNOWN_BLOCKED_REASON_SENTENCE =
  "we could not match it to this provider";

/**
 * The same sentence for an interest in ONE SERVICE of a provider's services
 * page, where the listing was judged against that service rather than the
 * provider's card. Same rule: never what was compared, never which side.
 */
export const UNKNOWN_BLOCKED_REASON_SENTENCE_SERVICE =
  "we could not match it to this service";

const UNKNOWN_BLOCKED_REASON_KEY = "unknown";

/**
 * The fit spine has to be readable with the colour switched off, so every row
 * states its verdict as a word as well as a hue. The three words map 1:1 onto
 * the backend's FitStatus (recruiter-engagement.gate.ts): 'ok' means both sides
 * stated something and they agree, 'warn' means someone left the field blank or
 * the two are not comparable, 'bad' means a stated conflict.
 *
 * A status this build has never heard of gets NO word rather than a guessed
 * one: an unrecognised verdict is not evidence of a good fit.
 */
export const FIT_STATUS_WORDS = {
  ok: "Fits",
  warn: "Unclear",
  bad: "Conflict",
};

/**
 * "$150/hr" / "$300,000/yr". Null when the listing states no usable figure, so
 * a row can omit the pay line rather than print "$0" or "undefined".
 */
export function formatListingRate(rate, rateUnit) {
  if (typeof rate !== "number" || !Number.isFinite(rate) || rate <= 0)
    return null;
  const suffix = rateUnit === "year" ? "/yr" : rateUnit === "hour" ? "/hr" : "";
  // Cents matter on an hourly rate and never on an annual one.
  const decimals = rateUnit === "hour" && !Number.isInteger(rate) ? 2 : 0;
  const amount = rate.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `$${amount}${suffix}`;
}

/**
 * The criteria the spine can actually render. The backend sends all five in a
 * fixed order and this preserves it; a criterion missing its label or its value
 * is DROPPED rather than rendered as a blank row, because a fit row with
 * nothing in it reads as "we checked and it was fine".
 *
 * Nothing here recomputes fit. Distance, the rate multiple and every verdict
 * were worked out by the pair gate on the server, which is the only place that
 * knows the provider's floor and radius. The client's whole job is to print
 * what it was handed.
 */
export function fitCriteria(listing) {
  const rows = Array.isArray(listing && listing.fit) ? listing.fit : [];
  return rows.filter(
    (c) =>
      c &&
      typeof c.label === "string" &&
      c.label.trim().length > 0 &&
      typeof c.value === "string" &&
      c.value.trim().length > 0,
  );
}

/**
 * The distance to show, in the server's own words.
 *
 * THE SPINE CRITERION IS THE ONLY SOURCE. It used to fall back to formatting
 * `listing.distanceMiles`, and that fallback is now deleted rather than left
 * unreachable: the server stopped sending the field precisely because an exact
 * distance is a measurement against the provider's sealed coordinates, and one
 * per facility triangulates. A dormant formatter is how a server-side
 * regression starts quietly printing mileage again.
 *
 * The criterion states a verdict now ("in the area they travel"), not a
 * figure, and it still distinguishes the two unknowns the number never could:
 * "no address on file" (the recruiter's own facility is missing coordinates,
 * theirs to fix) from "distance unknown".
 */
export function listingDistanceLabel(listing) {
  const criterion = fitCriteria(listing).find((c) => c.key === "distance");
  return criterion ? criterion.value : null;
}

/**
 * Groups blockedListings by reason, most common first, so the none-match
 * branch can lead with the reason that accounts for the most listings and list
 * the rest underneath. Array.prototype.sort is stable, so equal counts keep
 * the order the server sent them in.
 */
export function summarizeBlockedReasons(
  blockedListings,
  { serviceScoped = false } = {},
) {
  const rows = Array.isArray(blockedListings) ? blockedListings : [];
  const unknownSentence = serviceScoped
    ? UNKNOWN_BLOCKED_REASON_SENTENCE_SERVICE
    : UNKNOWN_BLOCKED_REASON_SENTENCE;
  const groups = new Map();

  rows.forEach((row) => {
    const code = row && row.blockedReason;
    const key = BLOCKED_REASON_SENTENCES[code]
      ? code
      : UNKNOWN_BLOCKED_REASON_KEY;
    const group = groups.get(key) || { reason: key, count: 0, titles: [] };
    group.count += 1;
    if (row && row.title) group.titles.push(row.title);
    groups.set(key, group);
  });

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      sentence: BLOCKED_REASON_SENTENCES[group.reason] || unknownSentence,
    }))
    .sort((a, b) => b.count - a.count);
}

const countLabel = (n) => (n === 1 ? "1 listing" : `${n} listings`);

/**
 * The conversations this company already has open with the provider, as the
 * server sent them on the entry: most recently active first, each with the
 * id `/company/pipeline/:id` opens. A row with no id is dropped, because it is
 * a conversation this modal could not open, and offering to open it would be
 * a button that goes nowhere.
 *
 * Empty for an older server and for a read the server failed open on, so both
 * fall through to the ordinary panels.
 */
export function openConversationsOf(entry) {
  const rows = Array.isArray(entry && entry.openConversations)
    ? entry.openConversations
    : [];
  return rows
    .filter(
      (row) =>
        row &&
        (typeof row.conversationId === "string" ||
          typeof row.conversationId === "number") &&
        String(row.conversationId).trim().length > 0,
    )
    .map((row) => ({
      conversationId: String(row.conversationId),
      title:
        typeof row.title === "string" && row.title.trim().length > 0
          ? row.title.trim()
          : null,
    }));
}

/**
 * The sentence that names the conversation "Open the conversation" opens. With
 * several, it is the most recent one, and the count says there are others.
 * No title means no name, never a placeholder.
 */
export function openConversationSentence(conversations) {
  const [latest] = conversations;
  if (!latest) return "";
  if (conversations.length === 1) {
    return latest.title
      ? `You have a conversation open with them on ${latest.title}.`
      : "You have a conversation open with them.";
  }
  const count = `You have ${conversations.length} conversations open with them.`;
  return latest.title ? `${count} The most recent is on ${latest.title}.` : count;
}

/**
 * "a" / "an" for the provider type in the title. The four provider types are
 * CRNA, CAA, SRNA and Anesthesiologist, and two of them ("an SRNA", "an
 * Anesthesiologist") take "an", so a hardcoded "a" ships visibly broken
 * English to half the population. Initialisms are judged by how the first
 * LETTER is said, not by whether it is a vowel.
 */
export function indefiniteArticle(word) {
  const w = String(word || "").trim();
  if (!w) return "a";
  const first = w[0].toUpperCase();
  const isInitialism = w.length > 1 && w === w.toUpperCase();
  if (isInitialism) return "AEFHILMNORSX".includes(first) ? "an" : "a";
  return "AEIOU".includes(first) ? "an" : "a";
}

/**
 * The role, rendered as THE listing card -- the same `FlippableListingCard` the
 * provider deck, the share landing (`/s/<token>`) and the map discovery surface
 * render, fed by the same `transformListingData`. It replaces a hand-rolled
 * mirror of the provider card that had already begun to drift: the recruiter
 * picking a role and the provider receiving it must be looking at one card, not
 * two implementations of the idea of a card.
 *
 * `summary.listing` is the listing document as the gate projected it for exactly
 * this component, with its facility resolved onto `listing.facility`. It is
 * REPORTING ONLY: the spine, the ranking and the send all still read the flat
 * summary fields beside it, so nothing about which role is offered depends on
 * this card.
 *
 * hideShareButton is not cosmetic. The card mints a share link for the listing
 * otherwise, and the server refuses to mint one for a viewer who is not sharing
 * it publicly -- the identical trap the provider card in this modal already
 * carries the flag for.
 *
 * `transformListingData` maps a FIXED field list and does not spread its input,
 * so a field the gate stops sending and a field the transform never carried are
 * indistinguishable on the card: a blank row. The projection is pinned on the
 * server side for that reason.
 */
function RoleCard({ listing }) {
  return (
    <article
      className="ascm-role"
      data-testid="role-card"
      aria-label={`Role: ${listing.title}`}
    >
      <FlippableListingCard
        profile={transformListingData(listing.listing || {})}
        hideShareButton
        // Same prop, same reason, as the provider card in the column opposite:
        // a fact the listing never stated is omitted rather than drawn as a
        // dash. It matters more here than on a deck, because this modal stands
        // the two cards side by side under one fixed height budget -- a role
        // that states no schedule pattern rendered its weekly hours as "- 40",
        // and a column of placeholder rows both reads as broken and spends
        // height the card beside it needs.
        hideEmptyRows
      />
    </article>
  );
}

/**
 * The middle column: one row per criterion the server scored, in the order the
 * server sent them. Colour is decoration only -- the verdict is spelled out in
 * a word on every row, so the spine survives greyscale, a colour-blind reader
 * and a screen reader identically.
 */
function FitSpine({ listing }) {
  const criteria = fitCriteria(listing);

  return (
    <div className="ascm-spine" data-testid="fit-spine">
      <p className="ascm-spine__title">{"How this role lines up"}</p>
      {criteria.map((c) => {
        const word = FIT_STATUS_WORDS[c.status];
        return (
          <div
            key={c.key || c.label}
            className={`ascm-fit ascm-fit--${
              FIT_STATUS_WORDS[c.status] ? c.status : "none"
            }`}
          >
            <span className="ascm-fit__text">
              <span className="ascm-fit__label">{c.label}</span>
              <span className="ascm-fit__value">{c.value}</span>
            </span>
            {word && <span className="ascm-fit__status">{word}</span>}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Shown to a GROUP user (mounted from CompanyLayout) who has expressed
 * interest in a provider on that provider's public share card (`/s/<token>`)
 * and has not yet sent them a listing. Lets them pick which of their own
 * listings to send -- restricted to listings that actually satisfy the
 * provider's stated preferences (matchingListings, computed server-side by
 * claim-anonymous through the same pair gate the anonymous share-card flow
 * uses). The server re-checks that gate at conversion time
 * (convertAnonymousSwipe), so this picker narrowing the options is a UX
 * convenience, not the enforcement.
 *
 * This modal does NOT claim a mutual match. The company expressed interest;
 * the provider has not reciprocated and will not until they see the listing.
 * Every string here has to keep that straight.
 *
 * The picker is a FACE-OFF: the provider's own card on the left, the role
 * rendered as its mirror on the right, and the server's fit spine between them.
 * A cycler steps through every matching role in the order the server ranked
 * them, and "See all n roles" swaps to the ranked list for anyone who would
 * rather scan than compare.
 *
 * Props:
 * - show, onHide: standard modal visibility. onHide fires on skip / ESC /
 *   click-away and must NOT clear the caller's pending interest. The interest
 *   now lives in a durable RecruiterInterest row, so there is always something
 *   to come back to.
 * - onClaimComplete: fires once every pending entry has had a listing sent.
 *   The caller clears anonymous data and toasts.
 * - pendingSwipes: pre-fetched by CompanyLayout from POST
 *   /swipes/claim-anonymous. The modal no longer issues that request itself --
 *   it used to duplicate the layout's call and block behind its own spinner.
 *   Each entry carries a REQUIRED interestId and an OPTIONAL anonymousSwipeId:
 *   the server builds the list from durable RecruiterInterest rows, so an
 *   entry whose 24h AnonymousSwipe has expired still arrives, just without one.
 * - authToken: bearer for the convert-anonymous POST, which is still ours.
 * - focusInterestId: when set, scopes the modal to that one interest (the
 *   pipeline's "Send a listing" CTA deep-links here with ?interest=<id>).
 * - onGoToListings: OPTIONAL. Fires when the recruiter takes a none-match
 *   panel's primary button ("Post a matching listing" / "Post a listing" /
 *   "Go to my listings"). The caller owns both the close and the navigation,
 *   because the caller is the only one that can stop its own claim effect from
 *   re-opening this modal on the page it just navigated to. Omit it and the
 *   modal closes and navigates to /company/listings itself.
 *
 *   Receives `{ intent }`: 'new' when the button promised a listing the
 *   recruiter does not have yet and the builder is the honest destination,
 *   'roster' when the job is to publish one they already have. A caller that
 *   ignores the argument keeps the old roster-only behaviour.
 * - onInterestDismissed: OPTIONAL. Fires with the interestId AFTER the server
 *   has confirmed the dismissal. Same report-don't-perform contract as
 *   onGoToListings and for a related reason: the caller owns `pendingSwipes`,
 *   which feeds both this queue and the Pipeline nav badge, so only it can
 *   drop the row from both at once. Omit it and the modal just advances its
 *   own queue; the server write has still happened either way.
 * - onOpenConversation: OPTIONAL. Fires with `(conversationId, { interestId,
 *   serviceId })` when the recruiter opens a conversation this interest
 *   duplicated, AFTER the server has confirmed the duplicate's dismissal. It
 *   is the only report for that action (onInterestDismissed does not also
 *   fire), and the caller owns the close, the queue and the navigation, for
 *   the same reason as onGoToListings: its claim effect must not re-open this
 *   modal on the conversation it just opened. Omit it and the modal closes
 *   and navigates there itself.
 */
function AnonymousSwipeClaimModal({
  show,
  onHide,
  onClaimComplete,
  pendingSwipes,
  authToken,
  focusInterestId,
  onGoToListings,
  onInterestDismissed,
  onOpenConversation,
}) {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [rawListingIndex, setRawListingIndex] = useState(0);
  const [view, setView] = useState("compare");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [allowanceWarning, setAllowanceWarning] = useState(false);
  // The dismissal is a two-step: the trigger arms the confirm row in place, and
  // only the confirm posts. Local to the entry on screen, and reset alongside
  // the other per-entry state below, so a half-armed confirm can never be
  // inherited by the NEXT provider in the queue and dismissed by one stray
  // click.
  const [confirmDismiss, setConfirmDismiss] = useState(false);

  const entries = useMemo(() => {
    const all = Array.isArray(pendingSwipes) ? pendingSwipes : [];
    if (!focusInterestId) return all;
    return all.filter((entry) => entry && entry.interestId === focusInterestId);
  }, [pendingSwipes, focusInterestId]);

  const total = entries.length;
  const currentSwipe = entries[currentIndex];
  const matchingListings = useMemo(
    () => (currentSwipe && currentSwipe.matchingListings) || [],
    [currentSwipe],
  );
  const blockedGroups = useMemo(
    () =>
      summarizeBlockedReasons(currentSwipe && currentSwipe.blockedListings, {
        serviceScoped: !!(currentSwipe && currentSwipe.serviceId),
      }),
    [currentSwipe],
  );
  const openConversations = useMemo(
    () => openConversationsOf(currentSwipe),
    [currentSwipe],
  );

  /**
   * Is the ONLY thing standing in the way that this company has already
   * engaged this provider on every listing it has?
   *
   * The none-match panel's heading is "None of your listings fit this
   * provider", which is a statement about FIT and is simply false here: those
   * listings may fit perfectly, they have just already been spent on this
   * provider (a negotiation is keyed per listing, so the same one cannot carry
   * a second). Telling a returning recruiter their roles do not fit someone
   * they already interviewed reads as a bug in the matching, and sends them off
   * to "post a matching listing" for a reason that was never true.
   *
   * Requires at least one blocked listing AND every group to be relational, so
   * a mixed board ("2 do not state pay, 1 already open") keeps the fit heading,
   * which is still the more useful thing to lead with there.
   */
  const blockedOnlyByExistingEngagement =
    blockedGroups.length > 0 &&
    blockedGroups.every(
      (group) =>
        group.reason === "negotiation-open" ||
        group.reason === "negotiation-closed",
    );


  // One stable key per entry so the reset effects below fire when the entry
  // actually changes, not on every re-render (the arrays arrive as fresh
  // object identities from the layout's fetch).
  //
  // interestId leads because it is the durable identifier: the RecruiterInterest
  // row has no TTL, while anonymousSwipeId is ABSENT on any entry whose 24h
  // AnonymousSwipe has already expired. The positional fallback covers a
  // malformed entry carrying neither, so two id-less entries cannot collapse
  // onto one key and leave the reset effect below unfired when the queue
  // advances between them.
  const keyOf = (entry, i) =>
    (entry && (entry.interestId || entry.anonymousSwipeId)) || `#${i}`;
  const entryKeys = entries.map(keyOf).join("|");
  const currentKey = currentSwipe ? keyOf(currentSwipe, currentIndex) : "";

  // A refetch upstream (or a change of focus) reshuffles the queue; start over
  // rather than pointing at whatever now sits at the old index.
  useEffect(() => {
    setCurrentIndex(0);
  }, [entryKeys, focusInterestId]);

  // A new provider means a new deck of roles: back to the first eligible role, back to the
  // face-off. Nothing is auto-sent here. Exactly one matching listing used to
  // convert itself the moment the modal mounted -- no confirmation, no choice,
  // nothing to cancel -- and the single-role face-off still ends at an explicit
  // "Send this role" for that reason.
  useEffect(() => {
    setRawListingIndex(0);
    setView("compare");
    setError("");
    setConfirmDismiss(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKey]);

  // Nothing to offer (an empty queue, or a ?interest= that no longer resolves).
  // Close rather than sit there rendering nothing while the caller believes a
  // modal is open. onHide, not onClaimComplete: nothing was sent.
  useEffect(() => {
    if (show && total === 0) onHide?.();
  }, [show, total, onHide]);

  if (total === 0 || !currentSwipe) return null;

  const providerProfile = currentSwipe.providerProfile;
  const providerType =
    (providerProfile && providerProfile.providerType) || "provider";
  const hasAnyListings = !!currentSwipe.hasAnyListings;
  const hasAnyNonLiveListings = !!currentSwipe.hasAnyNonLiveListings;
  const roleCount = matchingListings.length;
  // A heart on ONE SERVICE of a provider's services page. The title is that
  // service's place and dates, never a rate. Three states, and only `true`
  // opens the listing picker:
  //   true   the service is live, and listings were judged against it
  //   false  hidden, ended, paused or in Stealth Mode: nothing was judged
  //   other  the check itself failed, which is NOT evidence the service is
  //          gone, so it must never be offered for dismissal
  const serviceId = currentSwipe.serviceId || null;
  const serviceTitle = serviceId ? currentSwipe.serviceTitle || null : null;
  const serviceUnavailable =
    !!serviceId && currentSwipe.serviceAvailable === false;
  const serviceUnchecked =
    !!serviceId &&
    currentSwipe.serviceAvailable !== true &&
    currentSwipe.serviceAvailable !== false;
  // Contact permission and availability freshness are deliberately separate.
  // A stale calendar does not stop an employer from sending an interest; only
  // the server's coarse privacy/eligibility verdict does. Omission preserves
  // the pre-field contract during a rolling deploy. The old field is read only
  // as a fallback for a briefly mixed backend/frontend rollout.
  const providerCannotReceiveInterest =
    currentSwipe.providerCanReceiveInterest === false ||
    (currentSwipe.providerCanReceiveInterest == null &&
      currentSwipe.providerOpenToMatching === false);
  const providerAvailabilityUnconfirmed =
    currentSwipe.providerAvailabilityConfirmed === false;
  // Already talking to this provider: the interest only duplicates that
  // conversation, so it outranks every other state, a sendable role
  // included. The listing it needs is the one they are already discussing,
  // and the service it names (live or not) is theirs to raise in that thread.
  const openConversation = openConversations[0] || null;
  const alreadyTalking = !!openConversation;
  const hasMatches =
    !alreadyTalking &&
    !providerCannotReceiveInterest &&
    roleCount > 0 &&
    !serviceUnavailable &&
    !serviceUnchecked;

  // The index is clamped rather than trusted: the reset effect runs AFTER the
  // render that first sees a shorter deck, so an unclamped index would reach
  // for a listing that is not there for one frame.
  const listingIndex = hasMatches
    ? Math.min(rawListingIndex, roleCount - 1)
    : 0;
  const focusedListing = hasMatches ? matchingListings[listingIndex] : null;

  const title = `You're interested in ${indefiniteArticle(providerType)} ${providerType}${
    total > 1 ? ` (${currentIndex + 1} of ${total})` : ""
  }`;
  const progress = ((currentIndex + 1) / total) * 100;

  const advanceOrFinish = (done) => {
    if (currentIndex < total - 1) {
      setCurrentIndex(currentIndex + 1);
      setRawListingIndex(0);
      setView("compare");
      setError("");
      setConfirmDismiss(false);
    } else {
      done();
    }
  };

  // Wrapping in both directions: with a deck of fifteen, a recruiter who has
  // stepped to the last one should not have to walk back through fourteen.
  const stepRole = (delta) => {
    if (roleCount < 2) return;
    setRawListingIndex((i) => {
      const from = Math.min(i, roleCount - 1);
      return (from + delta + roleCount) % roleCount;
    });
    setError("");
  };

  const pickRole = (i) => {
    setRawListingIndex(i);
    setView("compare");
    setError("");
  };

  const handleSend = async () => {
    if (!focusedListing || !focusedListing.id) {
      setError("Pick a role first.");
      return;
    }

    // interestId is what the server prefers, and the only identifier that
    // survives: the RecruiterInterest row is durable, while the AnonymousSwipe
    // it was bound from expires after 24h. anonymousSwipeId therefore rides
    // along ONLY when this entry still has one -- it is optional now, and an
    // entry read back from RecruiterInterest after the swipe expired (or on a
    // second device, or after the local anon id was cleared) has none.
    const payload = { listingId: focusedListing.id };
    if (currentSwipe.interestId) payload.interestId = currentSwipe.interestId;
    if (currentSwipe.anonymousSwipeId) {
      payload.anonymousSwipeId = currentSwipe.anonymousSwipeId;
    }

    // The server needs at least one of the two. An entry with neither is
    // malformed data, not something the recruiter did wrong, and posting it
    // would come back as an opaque 400.
    if (!payload.interestId && !payload.anonymousSwipeId) {
      setError("We could not send that listing. Please refresh and try again.");
      return;
    }

    setSending(true);
    setError("");
    setAllowanceWarning(false);

    try {
      const res = await fetch(`${BACKEND_URL}/swipes/convert-anonymous`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        setAllowanceWarning(errorData.message?.code === "MATCH_WORK_ALLOWANCE_EXHAUSTED");
        throw new Error(apiErrorMessage(errorData, "We could not send that listing. Please try again."));
      }

      advanceOrFinish(() => onClaimComplete?.());
    } catch (err) {
      console.error("[AnonymousSwipeClaimModal] Error sending listing:", err);
      setError(
        err.message || "We could not send that listing. Please try again.",
      );
    } finally {
      setSending(false);
    }
  };

  // Skipping never deletes the interest -- it is a durable row now, so the
  // modal can offer it again on the next company route change.
  const handleSkip = () => advanceOrFinish(() => onHide?.());

  /**
   * The other half of skipping. Skip says "not today" and keeps the interest in
   * the queue; this says "there is nothing here I can send them" and takes it
   * out of both the queue and the pipeline's Interested column.
   *
   * The server stamps a soft `dismissedAt` -- nothing is deleted, and a fresh
   * expression of interest in the same provider clears it -- which is the only
   * reason the confirm copy is allowed to be as final as it is.
   */
  const handleDismiss = async () => {
    const interestId = currentSwipe && currentSwipe.interestId;

    // Unlike sending, there is no anonymousSwipeId fallback: a dismissal
    // addresses the durable row directly, and an entry without one is malformed
    // data rather than something the recruiter did wrong.
    if (!interestId) {
      setError("We could not dismiss that. Please refresh and try again.");
      return;
    }

    setSending(true);
    setError("");
    setAllowanceWarning(false);

    try {
      const res = await fetch(
        `${BACKEND_URL}/swipes/interest/${interestId}/dismiss`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            reason: blockedOnlyByExistingEngagement
              ? "already-engaged"
              : "no-fitting-listing",
          }),
        },
      );

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(
          apiErrorMessage(errorData, "We could not dismiss that. Please try again."),
        );
      }

      setConfirmDismiss(false);

      // EXACTLY ONE of these two, never both. When the caller owns the queue it
      // removes this row from `pendingSwipes`, which shortens `entries`,
      // changes `entryKeys` and sends the reset effect back to index 0 -- the
      // next provider renders on its own. Calling advanceOrFinish as well would
      // step PAST that provider, so a dismissal on a three-entry queue would
      // silently skip the one behind it.
      if (onInterestDismissed) {
        // The service rides along so the caller can say which kind of
        // interest it just dropped.
        onInterestDismissed(interestId, {
          serviceId: (currentSwipe && currentSwipe.serviceId) || null,
        });
        return;
      }

      advanceOrFinish(() => onHide?.());
    } catch (err) {
      console.error("[AnonymousSwipeClaimModal] Error dismissing:", err);
      setError(err.message || "We could not dismiss that. Please try again.");
    } finally {
      setSending(false);
    }
  };

  /**
   * "Open the conversation": this company is already talking to the provider,
   * so the interest is a duplicate of that conversation. Dismiss it (so it
   * stops being offered and leaves the Interested column), then go to the
   * conversation.
   *
   * Dismiss FIRST, and open nothing if it fails. The interest closes only by
   * its own stamp, so a navigation that outran a failed write would leave the
   * duplicate in the queue, and the claim effect would offer it again on the
   * very next route change. On a failure the recruiter stays here with the
   * button to retry; the board still has the conversation either way.
   */
  const handleOpenConversation = async () => {
    const interestId = currentSwipe && currentSwipe.interestId;
    const conversationId = openConversation && openConversation.conversationId;
    if (!interestId || !conversationId) {
      setError(
        "We could not open that conversation. Please refresh and try again.",
      );
      return;
    }

    setSending(true);
    setError("");
    setAllowanceWarning(false);

    try {
      const res = await fetch(
        `${BACKEND_URL}/swipes/interest/${interestId}/dismiss`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({ reason: "conversation-open" }),
        },
      );

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(
          apiErrorMessage(errorData, "We could not open that conversation. Please try again."),
        );
      }

      if (onOpenConversation) {
        onOpenConversation(conversationId, {
          interestId,
          serviceId: (currentSwipe && currentSwipe.serviceId) || null,
        });
        return;
      }

      onHide?.();
      navigate(negotiationLink([{ conversationId }]));
    } catch (err) {
      console.error(
        "[AnonymousSwipeClaimModal] Error opening the conversation:",
        err,
      );
      setError(
        err.message || "We could not open that conversation. Please try again.",
      );
    } finally {
      setSending(false);
    }
  };

  // Every none-match panel's primary button lands here: "Post a matching
  // listing", "Post a listing" and "Go to my listings" are the same decision
  // -- the recruiter is leaving to go work on their listings.
  //
  // REPORTED, not performed, when the caller supplies `onGoToListings`. The
  // caller has to know, because CompanyLayout re-offers this modal on every
  // company route change: a navigation the modal fires itself re-triggers that
  // claim effect, which re-opens this modal on top of the very page it just
  // sent the recruiter to. The second click then looked like it "worked" only
  // because navigating to the pathname you are already on does not change
  // `location.pathname`, so the effect never re-ran.
  //
  // The self-navigating fallback stays for callers that pass no handler
  // (GroupDashboardPage), so the button is never inert.
  //
  // `intent` distinguishes the two destinations the three labels actually
  // promise. "Post a matching listing" and "Post a listing" both promise a
  // listing that does not exist yet, so they open the manual builder
  // (?new=1, CompanyListingsPage's existing door into it); dropping the
  // recruiter on the roster made them go find the New listing button to do the
  // thing they had just pressed a button to do. "Go to my listings" promises
  // the opposite -- publish a listing they already have -- and stays on the
  // roster.
  const handleGoToListings = (intent = "roster") => {
    if (onGoToListings) {
      onGoToListings({ intent });
      return;
    }
    onHide?.();
    navigate(
      intent === "new" ? "/company/listings?new=1" : "/company/listings",
    );
  };

  const savedNote = (
    <p className="ascm-panel__note">We&apos;ll keep this saved for you.</p>
  );

  const rankedLabelId = "ascm-ranked-label";

  const providerColumn = providerProfile ? (
    <CRNAProfileCard
      profile={providerProfile}
      readOnly={true}
      useProvidedProfile={true}
      hideShareButton={true}
      skipVerification={true}
      // Freshness changes the promise on the card, never whether Send exists.
      // The server can confirm availability from a current schedule/service
      // even when the legacy profile date is absent, so its explicit false is
      // the authoritative reason to replace the date with cautious copy.
      availabilityLabel={
        providerAvailabilityUnconfirmed
          ? AVAILABILITY_NEEDS_CONFIRMATION
          : undefined
      }
      // Same reason ShareLanding passes it: sections the provider never filled
      // in are omitted, not rendered as a column of dashes. Without it the
      // face-off's left column runs far taller than the role card beside it,
      // padded out with empty rows that say nothing.
      hideEmptyRows
    />
  ) : (
    <div className="ascm-fallback-card">
      <h3 className="ascm-fallback-card__title">{providerType}</h3>
      <p className="ascm-fallback-card__text">
        {"We could not load this profile right now."}
      </p>
    </div>
  );

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      // Deliberately NOT `scrollable`: that gives the dialog a FIXED height of
      // the viewport less 3.5rem and hands the overflow to the body, which is
      // how this modal came to scroll as a whole on a laptop. The height is
      // capped at 86vh in CSS instead, the cards are sized off that same
      // budget, and the only thing that scrolls inside it is the ranked list.
      className="ascm-root"
      dialogClassName="ascm-dialog"
    >
      <Modal.Header closeButton>
        <Modal.Title as="h5">{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="ascm-body">
        {total > 1 && (
          <div className="ascm-progress" role="presentation">
            <div
              className="ascm-progress__bar"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        {serviceTitle && <p className="ascm-for">{`For: ${serviceTitle}`}</p>}

        {hasMatches && (
          <p className="ascm-subhead">
            {
              "Pick a role to send them. They'll see it and decide whether to connect."
            }
          </p>
        )}

        {error && (
          <div className={`ascm-error${allowanceWarning ? " ascm-error--warning" : ""}`} role="alert">
            <span>{error}</span>
            <button
              type="button"
              className="ascm-error__close"
              aria-label="Dismiss this message"
              onClick={() => setError("")}
            >
              &times;
            </button>
          </div>
        )}

        {hasMatches && view === "compare" && (
          <div className="ascm-faceoff">
            <section className="ascm-side">
              <p className="ascm-caption">{"They want"}</p>
              {providerColumn}
            </section>

            <section className="ascm-middle">
              <FitSpine listing={focusedListing} />
            </section>

            <section className="ascm-side">
              <p className="ascm-caption">{"You're offering"}</p>
              {/* Keyed on the listing so stepping the cycler REMOUNTS the card.
                  FlipCard owns its flipped state internally: without this, a
                  recruiter who turned one role over to read its case types
                  would land on the NEXT role's back face. */}
              <RoleCard key={focusedListing.id} listing={focusedListing} />

              {roleCount > 1 && (
                <div className="ascm-controls">
                  <div className="ascm-cycler">
                    <button
                      type="button"
                      className="ascm-cycler__arrow"
                      aria-label="Show the previous role"
                      onClick={() => stepRole(-1)}
                    >
                      <ChevronLeft size={18} aria-hidden="true" />
                    </button>
                    <span className="ascm-cycler__pos" aria-live="polite">
                      {`${listingIndex + 1} of ${roleCount}`}
                    </span>
                    <button
                      type="button"
                      className="ascm-cycler__arrow"
                      aria-label="Show the next role"
                      onClick={() => stepRole(1)}
                    >
                      <ChevronRight size={18} aria-hidden="true" />
                    </button>
                  </div>

                  <button
                    type="button"
                    className="ascm-linkbtn"
                    onClick={() => setView("all")}
                  >
                    {`See all ${roleCount} roles`}
                  </button>
                </div>
              )}
            </section>
          </div>
        )}

        {hasMatches && view === "all" && (
          <div className="ascm-ranked">
            <div className="ascm-ranked__head">
              <h3 className="ascm-ranked__title" id={rankedLabelId}>
                {`Your ${roleCount} matching roles`}
              </h3>
              <button
                type="button"
                className="ascm-linkbtn"
                onClick={() => setView("compare")}
              >
                {"Back to compare"}
              </button>
            </div>

            <div
              role="radiogroup"
              aria-labelledby={rankedLabelId}
              className="ascm-ranked__list"
            >
              {matchingListings.map((listing, i) => {
                const inputId = `ascm-role-${listing.id}`;
                const rate = formatListingRate(listing.rate, listing.rateUnit);
                const distance = listingDistanceLabel(listing);
                const cells = [distance, listing.contractType, rate].filter(
                  Boolean,
                );
                const isSelected = i === listingIndex;
                return (
                  <label
                    key={listing.id}
                    htmlFor={inputId}
                    className={`ascm-ranked__row${
                      isSelected ? " ascm-ranked__row--selected" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      className="ascm-ranked__radio"
                      id={inputId}
                      name="ascm-role-choice"
                      value={listing.id}
                      checked={isSelected}
                      onChange={() => pickRole(i)}
                    />
                    <span className="ascm-ranked__rank">{i + 1}</span>
                    <span className="ascm-ranked__main">
                      <span className="ascm-ranked__name">{listing.title}</span>
                      {listing.facilityName && (
                        <span className="ascm-ranked__facility">
                          {listing.facilityName}
                        </span>
                      )}
                      {cells.length > 0 && (
                        <span className="ascm-ranked__meta">
                          {cells.map((cell, cellIndex) => (
                            <span
                              className="ascm-ranked__cell"
                              key={`${listing.id}-cell-${cellIndex}`}
                            >
                              {cell}
                            </span>
                          ))}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {!hasMatches && (
          <div
            className={`ascm-noroles${
              alreadyTalking ? " ascm-noroles--talking" : ""
            }`}
          >
            <section className="ascm-side">
              <p className="ascm-caption">{"They want"}</p>
              {providerColumn}
            </section>

            <section className="ascm-noroles__panel">
              {alreadyTalking ? (
                <div className="ascm-panel ascm-panel--info">
                  <h3 className="ascm-panel__title">
                    {"You're already talking with this provider"}
                  </h3>
                  <p className="ascm-panel__text">
                    {openConversationSentence(openConversations)}
                  </p>
                  <p className="ascm-panel__text">
                    {
                      "Opening it also clears this interest from your pipeline, since that conversation already covers it. Nothing is sent to the provider."
                    }
                  </p>
                  <div className="ascm-panel__actions">
                    <button
                      type="button"
                      className="ascm-btn ascm-btn--primary"
                      onClick={handleOpenConversation}
                      disabled={sending}
                    >
                      {sending ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="ascm-btn__spinner"
                          />
                          {"Opening..."}
                        </>
                      ) : (
                        "Open the conversation"
                      )}
                    </button>
                  </div>
                </div>
              ) : providerCannotReceiveInterest ? (
                <div className="ascm-panel ascm-panel--info">
                  <h3 className="ascm-panel__title">
                    {"This interest can't be sent right now"}
                  </h3>
                  <p className="ascm-panel__text">
                    {
                      "Keep it saved and try again later, or dismiss it to take it out of your pipeline."
                    }
                  </p>
                  {confirmDismiss ? (
                    <div className="ascm-panel__confirm">
                      <p className="ascm-panel__text">
                        {serviceId
                          ? "Dismiss this interest? We'll take this service out of your pipeline. Nothing is sent to the provider."
                          : "Dismiss this interest? We'll take this provider out of your pipeline. Nothing is sent to them."}
                      </p>
                      <div className="ascm-panel__actions">
                        <button
                          type="button"
                          className="ascm-btn ascm-btn--secondary"
                          onClick={handleDismiss}
                          disabled={sending}
                        >
                          {sending ? "Dismissing..." : "Yes, dismiss it"}
                        </button>
                        <button
                          type="button"
                          className="ascm-btn ascm-btn--ghost"
                          onClick={() => setConfirmDismiss(false)}
                          disabled={sending}
                        >
                          {"Keep it"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="ascm-panel__actions">
                      <button
                        type="button"
                        className="ascm-btn ascm-btn--ghost"
                        onClick={() => setConfirmDismiss(true)}
                        disabled={sending}
                      >
                        {"Dismiss this interest"}
                      </button>
                    </div>
                  )}
                </div>
              ) : serviceUnchecked ? (
                <div className="ascm-panel ascm-panel--info">
                  <h3 className="ascm-panel__title">
                    {"We could not check this service right now"}
                  </h3>
                  <p className="ascm-panel__text">
                    {
                      "Skip it for now and we'll offer it again. Your interest is saved."
                    }
                  </p>
                </div>
              ) : serviceUnavailable ? (
                <div className="ascm-panel ascm-panel--info">
                  <h3 className="ascm-panel__title">
                    {"This service is not available right now"}
                  </h3>
                  <p className="ascm-panel__text">
                    {
                      "There is nothing to send for it right now. Skip it for now, or dismiss it to take it out of your pipeline."
                    }
                  </p>
                  {confirmDismiss ? (
                    <div className="ascm-panel__confirm">
                      <p className="ascm-panel__text">
                        {
                          "Dismiss this interest? We'll take this service out of your pipeline. Nothing is sent to the provider."
                        }
                      </p>
                      <div className="ascm-panel__actions">
                        <button
                          type="button"
                          className="ascm-btn ascm-btn--secondary"
                          onClick={handleDismiss}
                          disabled={sending}
                        >
                          {sending ? "Dismissing..." : "Yes, dismiss it"}
                        </button>
                        <button
                          type="button"
                          className="ascm-btn ascm-btn--ghost"
                          onClick={() => setConfirmDismiss(false)}
                          disabled={sending}
                        >
                          {"Keep it"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="ascm-panel__actions">
                      <button
                        type="button"
                        className="ascm-btn ascm-btn--ghost"
                        onClick={() => setConfirmDismiss(true)}
                        disabled={sending}
                      >
                        {"Dismiss this interest"}
                      </button>
                    </div>
                  )}
                </div>
              ) : !hasAnyListings && hasAnyNonLiveListings ? (
                <div className="ascm-panel ascm-panel--info">
                  <h3 className="ascm-panel__title">
                    {"Your listings are not published yet"}
                  </h3>
                  <p className="ascm-panel__text">
                    {serviceId
                      ? "You have listings, but none of them are live yet. Publish one and we'll check it against this service automatically."
                      : "You have listings, but none of them are live yet. Publish one and we'll check it against this provider automatically."}
                  </p>
                  <button
                    type="button"
                    className="ascm-btn ascm-btn--primary"
                    onClick={() => handleGoToListings("roster")}
                  >
                    {"Go to my listings"}
                  </button>
                  {savedNote}
                </div>
              ) : !hasAnyListings ? (
                <div className="ascm-panel ascm-panel--info">
                  <h3 className="ascm-panel__title">
                    {"You don't have any listings yet"}
                  </h3>
                  <p className="ascm-panel__text">
                    {serviceId
                      ? "Post a listing to reach this provider. We'll check it against this service automatically."
                      : "Post a listing to reach this provider. We'll check it against their location and pay preferences automatically."}
                  </p>
                  <button
                    type="button"
                    className="ascm-btn ascm-btn--primary"
                    onClick={() => handleGoToListings("new")}
                  >
                    {"Post a listing"}
                  </button>
                  {savedNote}
                </div>
              ) : (
                <div className="ascm-panel ascm-panel--warn">
                  <h3 className="ascm-panel__title">
                    {blockedOnlyByExistingEngagement
                      ? "You've already engaged this provider"
                      : serviceId
                        ? "None of your listings fit this service"
                        : "None of your listings fit this provider"}
                  </h3>
                  {blockedGroups.length > 0 ? (
                    <>
                      <p className="ascm-panel__text">
                        {`${countLabel(blockedGroups[0].count)}: ${blockedGroups[0].sentence}.`}
                      </p>
                      {blockedGroups.length > 1 && (
                        <ul className="ascm-panel__list">
                          {blockedGroups.slice(1).map((group) => (
                            <li key={group.reason}>
                              {`${countLabel(group.count)}: ${group.sentence}.`}
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : (
                    <p className="ascm-panel__text">
                      {serviceId
                        ? "None of your live listings cleared our checks for this service."
                        : "None of your live listings cleared our checks for this provider."}
                    </p>
                  )}
                  {/* The one fact that changes the advice in this panel: a
                      listing already written and waiting to be published. Said
                      in the same words the sibling panel uses for the same
                      fact, and only in the already-engaged branch -- in the
                      fit-failure branch a draft is no help, because whatever
                      stopped the live listings would stop it too. */}
                  {blockedOnlyByExistingEngagement && hasAnyNonLiveListings && (
                    <p className="ascm-panel__text">
                      {serviceId
                        ? "You have a listing that is not published yet. Publish it and we'll check it against this service automatically."
                        : "You have a listing that is not published yet. Publish it and we'll check it against this provider automatically."}
                    </p>
                  )}
                  {confirmDismiss ? (
                    <div className="ascm-panel__confirm">
                      <p className="ascm-panel__text">
                        {blockedOnlyByExistingEngagement
                          ? // Service first inside this branch too. A dismissal
                            // closes only this one interest, so a service's
                            // confirm must not read as dropping the provider.
                            serviceId
                            ? "Dismiss this interest? You have already worked every live listing you have with this provider. We'll stop offering this service and take it out of your pipeline. Nothing is sent to the provider."
                            : "Dismiss this interest? You have already worked every live listing you have with this provider. We'll take them out of your pipeline. Nothing is sent to them."
                          : serviceId
                            ? "Dismiss this interest? We'll stop offering this service and take it out of your pipeline. Nothing is sent to the provider."
                            : "Dismiss this interest? We'll stop offering this provider and take them out of your pipeline. Nothing is sent to them."}
                      </p>
                      <p className="ascm-panel__note">
                        {serviceId
                          ? "If you post a listing that fits and show interest again, it will come back."
                          : "If you post a listing that fits and show interest again, they'll come back."}
                      </p>
                      <div className="ascm-panel__actions">
                        <button
                          type="button"
                          className="ascm-btn ascm-btn--secondary"
                          onClick={handleDismiss}
                          disabled={sending}
                        >
                          {sending ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="ascm-btn__spinner"
                              />
                              {"Dismissing..."}
                            </>
                          ) : (
                            "Yes, dismiss it"
                          )}
                        </button>
                        <button
                          type="button"
                          className="ascm-btn ascm-btn--ghost"
                          onClick={() => setConfirmDismiss(false)}
                          disabled={sending}
                        >
                          {"Keep it"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="ascm-panel__actions">
                        <button
                          type="button"
                          className="ascm-btn ascm-btn--primary"
                          onClick={() =>
                            handleGoToListings(
                              blockedOnlyByExistingEngagement &&
                                hasAnyNonLiveListings
                                ? // They have a listing written but not
                                  // published. Publishing it is less work than
                                  // writing another, and the roster is the only
                                  // surface that shows an unpublished one.
                                  "roster"
                                : // Nothing left to send and nothing waiting to
                                  // be published, so the listing that reaches
                                  // this provider does not exist yet.
                                  "new",
                            )
                          }
                        >
                          {blockedOnlyByExistingEngagement
                            ? // "Send them a different listing" USED TO BE HERE,
                              // and it named an action this branch has already
                              // ruled out. Reaching it means zero matching
                              // listings AND every blocked one already spent on
                              // this provider -- and the two arrays are an
                              // exhaustive, uncapped partition of the group's
                              // live listings, so there is no unused live
                              // listing left. A negotiation is keyed
                              // (provider, listing, initiatedBy) behind a real
                              // unique index, so the ones they have cannot
                              // carry a second one either. The button sent them
                              // to a roster with nothing on it they could use.
                              //
                              // Both replacements are actions that work: a
                              // newly published or newly posted listing is a
                              // new pair, and a new pair can carry a real
                              // negotiation with the same provider.
                              hasAnyNonLiveListings
                              ? "Publish another listing"
                              : "Post another listing"
                            : "Post a matching listing"}
                        </button>
                        {/* THE WAY OUT OF A STUCK ROW. Only here, in the panel
                            where live listings were actually tested against
                            this provider and none could be offered. The two
                            panels above never got that far -- there was nothing
                            live to test -- so a recruiter there is one listing
                            away from converting, and offering to throw the
                            interest away is the wrong advice. */}
                        <button
                          type="button"
                          className="ascm-btn ascm-btn--ghost"
                          onClick={() => setConfirmDismiss(true)}
                          disabled={sending}
                        >
                          {"Dismiss this interest"}
                        </button>
                      </div>
                      {savedNote}
                    </>
                  )}
                </div>
              )}
            </section>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="ascm-footer">
        <button
          type="button"
          className="ascm-btn ascm-btn--secondary"
          onClick={handleSkip}
          disabled={sending}
        >
          {"Skip for now"}
        </button>
        {hasMatches && (
          <button
            type="button"
            className="ascm-btn ascm-btn--primary"
            onClick={handleSend}
            disabled={!focusedListing || sending}
          >
            {sending ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="ascm-btn__spinner"
                />
                {"Sending..."}
              </>
            ) : (
              "Send this role"
            )}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default AnonymousSwipeClaimModal;
